import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Package, RefreshCw, RotateCcw, X, Truck, Loader2, ExternalLink, Plug } from "lucide-react";
import { listOrders, refundOrder, fulfillOrder, labelUrl, testCanadaPostConnection } from "../lib/api";

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "refunded", label: "Refunded" },
  { key: "unpaid", label: "Unpaid" },
];

function RefundDialog({ order, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("requested_by_customer");
  const [busy, setBusy] = useState(false);

  const isPartial = useMemo(() => {
    const v = parseFloat(amount);
    return !!v && v > 0 && v < order.amount;
  }, [amount, order.amount]);

  const handleRefund = async () => {
    setBusy(true);
    try {
      const payload = { reason };
      if (isPartial) payload.amount = parseFloat(amount);
      const res = await refundOrder(order.session_id, payload);
      toast.success(`Refund issued ${fmtUSD(res.amount)}`, {
        description: res.partial ? "Partial refund — inventory not restocked" : `Restocked ${res.restocked_lines} line(s)`,
      });
      onDone();
    } catch (e) {
      toast.error("Refund failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="refund-dialog">
      <div className="bg-white max-w-md w-full border border-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div>
            <div className="micro-label">REFUND</div>
            <div className="font-black text-lg tracking-tight" style={{ fontFamily: "Outfit" }}>
              {order.session_id.slice(0, 18)}…
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100" data-testid="refund-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="micro-label">ORDER TOTAL</div>
              <div className="font-black text-xl" style={{ fontFamily: "Outfit" }}>{fmtUSD(order.amount)}</div>
            </div>
            <div>
              <div className="micro-label">PLACED</div>
              <div className="text-sm">{fmtDate(order.created_at)}</div>
            </div>
          </div>
          <div>
            <label className="micro-label block mb-1">REFUND AMOUNT (LEAVE EMPTY FOR FULL)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max={order.amount}
              placeholder={`Full refund: ${fmtUSD(order.amount)}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-zinc-300 px-3 h-10 text-sm"
              data-testid="refund-amount"
            />
            {isPartial && (
              <div className="text-[11px] text-amber-700 mt-1">
                Partial refund — inventory will NOT be restocked.
              </div>
            )}
          </div>
          <div>
            <label className="micro-label block mb-1">REASON</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-zinc-300 px-3 h-10 text-sm"
              data-testid="refund-reason"
            >
              <option value="requested_by_customer">Requested by customer</option>
              <option value="duplicate">Duplicate</option>
              <option value="fraudulent">Fraudulent</option>
            </select>
          </div>
        </div>
        <div className="p-5 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 text-xs uppercase tracking-wider font-bold border border-zinc-300 hover:bg-zinc-50" data-testid="refund-cancel">
            Cancel
          </button>
          <button
            onClick={handleRefund}
            disabled={busy}
            className="px-4 h-10 text-xs uppercase tracking-wider font-bold bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-2"
            data-testid="refund-confirm"
          >
            {busy ? "Processing…" : "Issue Refund"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderRow({ order, onRefund, onChanged }) {
  const [fulfilling, setFulfilling] = useState(false);
  const items = order.line_items || [];
  const itemsLabel = items
    .map((i) => {
      const extras = [i.color, i.fragrance].filter(Boolean).join(" · ");
      return `${i.quantity}× ${i.name}${extras ? ` (${extras})` : ""}`;
    })
    .join(", ");
  const region = items[0]?.region || order.region || "—";

  let badge = { text: "INITIATED", cls: "bg-zinc-200 text-zinc-700" };
  if (order.refunded) badge = { text: "REFUNDED", cls: "bg-amber-100 text-amber-800 border border-amber-300" };
  else if (order.payment_status === "paid") badge = { text: "PAID", cls: "bg-emerald-500 text-white" };
  else if (order.payment_status === "unpaid") badge = { text: "UNPAID", cls: "bg-zinc-100 text-zinc-600" };

  const canRefund = order.payment_status === "paid" && !order.refunded;
  const fulfillment = order.fulfillment || null;
  const canFulfill = order.payment_status === "paid" && !order.refunded && !fulfillment?.tracking_pin;

  const handleFulfill = async () => {
    setFulfilling(true);
    try {
      const res = await fulfillOrder(order.session_id);
      toast.success("Shipping label created", {
        description: `Tracking ${res.tracking_pin}${res.email_sent ? " · customer emailed" : ""}`,
      });
      onChanged?.();
    } catch (e) {
      toast.error("Label creation failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setFulfilling(false);
    }
  };

  return (
    <tr className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`order-row-${order.session_id}`}>
      <td className="p-4 align-top">
        <div className="font-mono text-xs text-zinc-600">{order.session_id.slice(0, 18)}…</div>
        <div className="text-[11px] text-zinc-400 mt-0.5">{fmtDate(order.created_at)}</div>
      </td>
      <td className="p-4 align-top max-w-md">
        <div className="text-sm text-zinc-800 truncate" title={itemsLabel}>{itemsLabel || "—"}</div>
        <div className="text-[11px] text-zinc-400 mt-0.5">{items.length} item(s)</div>
        {(order.shipping_address || order.customer_email) && (
          <div className="mt-2 pt-2 border-t border-zinc-100" data-testid={`ship-to-${order.session_id}`}>
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-0.5">Ship to</div>
            {order.shipping_address?.name && (
              <div className="text-[11px] font-semibold text-zinc-700">{order.shipping_address.name}</div>
            )}
            {order.shipping_address && (
              <div className="text-[11px] text-zinc-500 leading-snug">
                {[order.shipping_address.line1, order.shipping_address.line2].filter(Boolean).join(", ")}<br />
                {[order.shipping_address.city, order.shipping_address.state, order.shipping_address.postal_code]
                  .filter(Boolean).join(", ")} {order.shipping_address.country}
              </div>
            )}
            {order.customer_email && (
              <div className="text-[11px] text-zinc-400 mt-0.5">{order.customer_email}</div>
            )}
          </div>
        )}
      </td>
      <td className="p-4 align-top">
        <span className="text-[10px] uppercase tracking-wider font-bold bg-zinc-900 text-white px-2 py-0.5">{region}</span>
      </td>
      <td className="p-4 align-top tabular-nums font-black" style={{ fontFamily: "Outfit" }}>
        {fmtUSD(order.amount)}
        {order.refund_amount && (
          <div className="text-[11px] text-amber-700 font-normal" style={{ fontFamily: "inherit" }}>
            -{fmtUSD(order.refund_amount)} refunded
          </div>
        )}
      </td>
      <td className="p-4 align-top">
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${badge.cls}`}>
          {badge.text}
        </span>
      </td>
      <td className="p-4 align-top text-right">
        <div className="flex flex-col items-end gap-2">
          {canRefund && (
            <button
              onClick={() => onRefund(order)}
              className="text-xs uppercase tracking-wider font-bold px-3 h-9 border border-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors inline-flex items-center gap-1.5"
              data-testid={`refund-${order.session_id}`}
            >
              <RotateCcw size={12} strokeWidth={2.5} />
              Refund
            </button>
          )}
          {canFulfill && (
            <button
              onClick={handleFulfill}
              disabled={fulfilling}
              className="text-xs uppercase tracking-wider font-bold px-3 h-9 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`fulfill-${order.session_id}`}
            >
              {fulfilling ? <Loader2 size={12} className="animate-spin" /> : <Truck size={12} strokeWidth={2.5} />}
              {fulfilling ? "Creating…" : "Create Label"}
            </button>
          )}
          {fulfillment?.tracking_pin && (
            <div className="text-right" data-testid={`tracking-${order.session_id}`}>
              <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-zinc-400">{fulfillment.carrier || "Carrier"}</div>
              <div className="text-[11px] font-mono font-bold">{fulfillment.tracking_pin}</div>
              <div className="flex items-center gap-2 justify-end mt-0.5">
                {fulfillment.label_url && (
                  <a href={labelUrl(order.session_id)} target="_blank" rel="noreferrer"
                     className="text-[10px] text-[#002FA7] font-semibold inline-flex items-center gap-1 hover:underline"
                     data-testid={`label-${order.session_id}`}>
                    <ExternalLink size={10} /> Label
                  </a>
                )}
                {fulfillment.tracking_link && (
                  <a href={fulfillment.tracking_link} target="_blank" rel="noreferrer"
                     className="text-[10px] text-zinc-500 inline-flex items-center gap-1 hover:underline">
                    Track
                  </a>
                )}
              </div>
            </div>
          )}
          {!canRefund && !canFulfill && !fulfillment?.tracking_pin && (
            <span className="text-[11px] text-zinc-400">—</span>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function OrdersPage() {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  const [refundTarget, setRefundTarget] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [cpTesting, setCpTesting] = useState(false);
  const [cpStatus, setCpStatus] = useState(null);

  const testConnection = async () => {
    setCpTesting(true);
    setCpStatus(null);
    try {
      const res = await testCanadaPostConnection();
      setCpStatus(res);
      if (res.ok) {
        toast.success("Canada Post connected", { description: res.message });
      } else {
        toast.error("Canada Post connection failed", { description: res.message });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message;
      setCpStatus({ ok: false, message: msg });
      toast.error("Canada Post connection failed", { description: msg });
    } finally {
      setCpTesting(false);
    }
  };

  const load = async () => {
    setRefreshing(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await listOrders(params);
      setOrders(res);
    } catch (e) {
      toast.error("Failed to load orders", { description: e?.message });
      setOrders([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="orders-page">
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="micro-label mb-3">FULFILMENT</div>
          <h1 className="text-5xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>
            Orders
          </h1>
          <p className="text-zinc-600 mt-2 max-w-xl">
            All Stripe checkout transactions. Issue full or partial refunds inline — full refunds re-credit regional stock.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={testConnection}
            disabled={cpTesting}
            className="text-xs uppercase tracking-wider font-bold px-3 h-10 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="test-canadapost-button"
          >
            {cpTesting ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} strokeWidth={2.5} />}
            {cpTesting ? "Testing…" : "Test Canada Post Connection"}
          </button>
          <button
            onClick={load}
            disabled={refreshing}
            className="text-xs uppercase tracking-wider font-bold px-3 h-10 border border-zinc-300 hover:bg-zinc-50 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="orders-refresh"
          >
            <RefreshCw size={14} strokeWidth={2.5} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {cpStatus && (
        <div
          className={`mb-6 px-4 py-3 border text-sm flex items-start gap-2 ${
            cpStatus.ok
              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
              : "border-red-300 bg-red-50 text-red-800"
          }`}
          data-testid="canadapost-status"
        >
          <Plug size={15} strokeWidth={2.5} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-[11px] uppercase tracking-wider">
              {cpStatus.ok ? "Canada Post — Connected" : "Canada Post — Not Connected"}
            </div>
            <div className="text-[13px]">{cpStatus.message}</div>
            {(cpStatus.host || cpStatus.customer_number) && (
              <div className="text-[11px] opacity-70 mt-0.5">
                {cpStatus.host} · customer #{cpStatus.customer_number || "—"} · {cpStatus.quote_type || ""}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="border border-zinc-200 bg-white flex items-center" data-testid="orders-filter">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
            className={`px-4 h-11 text-[11px] uppercase tracking-wider font-bold border-r border-zinc-200 transition-colors ${
              filter === f.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="border border-zinc-200 border-t-0 bg-white">
        {orders === null && (
          <div className="p-12 micro-label text-center" data-testid="orders-loading">LOADING ORDERS...</div>
        )}
        {orders && orders.length === 0 && (
          <div className="p-16 text-center" data-testid="orders-empty">
            <Package size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={2} />
            <p className="text-zinc-600">No orders in this view yet.</p>
          </div>
        )}
        {orders && orders.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="orders-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="p-4 micro-label">SESSION</th>
                  <th className="p-4 micro-label">ITEMS</th>
                  <th className="p-4 micro-label">REGION</th>
                  <th className="p-4 micro-label">AMOUNT</th>
                  <th className="p-4 micro-label">STATUS</th>
                  <th className="p-4 micro-label text-right">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <OrderRow key={o.session_id} order={o} onRefund={setRefundTarget} onChanged={load} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {refundTarget && (
        <RefundDialog
          order={refundTarget}
          onClose={() => setRefundTarget(null)}
          onDone={() => {
            setRefundTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}
